import { History } from 'history';
import React, { useEffect, useRef, useState } from 'react';
import { Arrow, Label, Layer, Line, Rect, Stage, Tag, Text } from 'react-konva';
import { useHistory, useParams } from 'react-router';
import { StepProps } from '../../stores/StepsStore';
import { cleanString } from '../../util/renderHelpers';

type FlowChartProps = {
  children?: never;
  steps: StepProps[] | null;
  isMobile: boolean;
};

type FlowChartStepProps = {
  children?: never;
  chartRef: any;
  stepPosition: { x: number; y: number };
  stepName: string;
  history: History;
  stepParams: string | undefined;
  stepActive: boolean;
  stepPath: string;
  stepColor: string;
  stepId: string;
  stepType: string;
  isMobile: boolean;
};

type Arrow = {
  customLine: number[] | null;
  height: number;
  parentHeight: number;
  parentX: number;
  parentY: number;
  straightLine: boolean | string;
  x: number;
  y: number;
};

type Arrows = Arrow[];

type ArrowProps = {
  arrow: Arrow;
};

/**
 * Step box
 */
const FlowChartStep: React.FC<FlowChartStepProps> = ({
  chartRef,
  stepPosition,
  stepName,
  history,
  stepActive,
  stepPath,
  stepColor,
  stepId,
  stepType,
  stepParams,
  isMobile,
}) => {
  const [hover, setHover] = useState(false);
  const [textHeight, setTextHeight] = useState(0);

  useEffect(() => {
    if (chartRef.current) {
      if (stepActive) {
        window.scrollTo(0, stepPosition.y);
      }
    }
  }, [chartRef, stepActive, stepPosition, stepParams]);

  const clickHandler = () => {
    history.push(stepPath);
  };

  const mouseOverHandler = () => {
    if (chartRef.current) {
      chartRef.current.style.cursor = 'pointer';
    }
    setHover(true);
  };

  const mouseLeaveHandler = () => {
    if (chartRef.current) {
      chartRef.current.style.cursor = isMobile ? 'initial' : 'ew-resize';
    }
    setHover(false);
  };

  return (
    <Label
      id={`step-label-${stepId}`}
      onClick={clickHandler}
      onTap={clickHandler}
      onMouseLeave={mouseLeaveHandler}
      onMouseEnter={mouseOverHandler}
      x={stepPosition.x}
      y={stepPosition.y}
    >
      {/* Background */}
      <Tag
        cornerRadius={stepType === 'page' ? 5 : 30}
        fill={hover || stepActive || stepType !== 'page' ? stepColor : '#ffffff'}
        id={`step-tag-${stepId}`}
        shadowBlur={5}
        shadowColor="black"
        shadowOffset={{ x: 0, y: 4 }}
        shadowOpacity={0.27}
        height={textHeight === 0 ? undefined : textHeight < 60 ? 60 : textHeight}
      />
      {/* Title */}
      <Text
        align="center"
        fill={hover || stepActive || stepType !== 'page' ? '#ffffff' : '#404040'}
        fontFamily="VIC"
        fontSize={11}
        fontStyle="600"
        id={`step-text-${stepId}`}
        lineHeight={1.31}
        padding={15}
        text={stepName}
        verticalAlign="middle"
        width={130}
        wrap="word"
        height={textHeight === 0 ? undefined : textHeight < 60 ? 60 : textHeight}
        ref={node => {
          if (node) {
            setTextHeight(node.height() < 60 ? 60 : node.height());
          }
        }}
      />
      {/* Left side coloured line */}
      {stepType === 'page' && (
        <Rect width={5} height={textHeight} fill={stepColor} cornerRadius={[5, 0, 0, 5]} />
      )}
    </Label>
  );
};

const FlowChartCanvas: React.FC<FlowChartProps> = ({ steps, isMobile }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { step } = useParams();
  const [diagramSize, setDiagramSize] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
  });

  useEffect(() => {
    const current = chartRef.current;
    if (steps && current) {
      let maxHeight = 0;
      let maxWidth = 0;
      steps.forEach(stepNode => {
        maxHeight = Math.max(
          maxHeight,
          stepNode.stepPos ? stepNode.stepPos.y : current.getBoundingClientRect().height
        );

        maxWidth = Math.max(
          maxWidth,
          stepNode.stepPos ? stepNode.stepPos.x : current.getBoundingClientRect().width
        );
      });

      setDiagramSize({
        height: maxHeight + 100,
        width: maxWidth + 200,
      });
    }
  }, [setDiagramSize, steps]);

  const dragHandler = (pos: any) => ({
    x: pos.x,
    y: 0,
  });

  const getColor: { [key: string]: string } = {
    green: '#6d9d13',
    grey: '#a39191',
    orange: '#ff9062',
    purple: '#833c5f',
    red: '#c64b4b',
  };

  const setStepHeight = (length: number) => {
    if (length <= 31) {
      return 60;
    } else if (length > 31 && length <= 50) {
      return 74;
    } else if (length >= 65) {
      return 104;
    } else {
      return 88;
    }
  };

  const displaySteps =
    steps && diagramSize
      ? steps.map(stepNode => {
          return (
            <FlowChartStep
              key={`flowChartStep-${stepNode.stepId}`}
              chartRef={chartRef}
              history={history}
              stepParams={step}
              stepId={stepNode.stepId}
              stepType={stepNode.stepType}
              stepPath={stepNode.stepPath}
              stepColor={getColor[`${stepNode.stepColor}`]}
              stepName={cleanString(stepNode.stepName)}
              stepActive={
                step
                  ? stepNode.stepPath.indexOf(step) !== -1 &&
                    step.indexOf(stepNode.stepPath.split('/').reverse()[0]) !== -1
                  : false
              }
              stepPosition={
                stepNode.stepPos ? { x: stepNode.stepPos.x, y: stepNode.stepPos.y } : { x: 0, y: 0 }
              }
              isMobile={isMobile}
            />
          );
        })
      : null;

  const arrowsState =
    steps &&
    steps.reduce(
      (arrowNode, stepNode) => {
        const parentsIds = stepNode.stepParents;
        if (parentsIds && parentsIds.length > 0) {
          const parentsPos = parentsIds.map(parentId => {
            const parentStep = steps.find(findStep => findStep.stepId === String(parentId));
            if (parentStep && parentStep.stepPos) {
              return {
                customLine: parentStep.stepCustomLine
                  ? parentStep.stepCustomLine[stepNode.stepId]
                  : null,
                height: setStepHeight(cleanString(stepNode.stepName).length),
                parentHeight: setStepHeight(cleanString(parentStep.stepName).length),
                parentX: parentStep.stepPos.x,
                parentY: parentStep.stepPos.y,
                straightLine: parentStep.stepStraightLine,
                x: stepNode.stepPos ? stepNode.stepPos.x : 0,
                y: stepNode.stepPos ? stepNode.stepPos.y : 0,
              };
            } else {
              return {
                customLine: null,
                height: 0,
                parentHeight: 0,
                parentX: 0,
                parentY: 0,
                straightLine: false,
                x: 0,
                y: 0,
              };
            }
          });
          return [...arrowNode, ...parentsPos];
        }
        return arrowNode;
      },
      [] as Arrows
    );

  const UpArrow: React.FC<ArrowProps> = ({ arrow }) => (
    <Arrow
      points={[arrow.parentX + 65, arrow.parentY - 2, arrow.x + 65, arrow.y + arrow.height + 2]}
      pointerLength={5}
      pointerWidth={5}
      fill="#a39191"
      stroke="#a39191"
      strokeWidth={1}
      dash={[4, 2]}
    />
  );

  const DownArrow: React.FC<ArrowProps> = ({ arrow }) => (
    <Arrow
      points={[
        arrow.parentX + 65,
        arrow.parentY + arrow.parentHeight + 2,
        arrow.x + 65,
        arrow.y - 2,
      ]}
      pointerLength={5}
      pointerWidth={5}
      fill="#a39191"
      stroke="#a39191"
      strokeWidth={1}
      dash={[4, 2]}
    />
  );

  const RightArrow: React.FC<ArrowProps> = ({ arrow }) => (
    <Arrow
      points={[
        arrow.parentX + 130 + 2,
        arrow.parentY + arrow.parentHeight / 2,
        arrow.x - 2,
        arrow.y + arrow.height / 2,
      ]}
      pointerLength={5}
      pointerWidth={5}
      fill="#a39191"
      stroke="#a39191"
      strokeWidth={1}
      dash={[4, 2]}
    />
  );

  const LeftArrow: React.FC<ArrowProps> = ({ arrow }) => (
    <Arrow
      points={[
        arrow.parentX - 2,
        arrow.parentY + arrow.parentHeight / 2,
        arrow.x + 130 + 2,
        arrow.y + arrow.height / 2,
      ]}
      pointerLength={5}
      pointerWidth={5}
      fill="#a39191"
      stroke="#a39191"
      strokeWidth={1}
      dash={[4, 2]}
    />
  );

  const RightDownArrow: React.FC<ArrowProps> = ({ arrow }) => (
    <>
      <Arrow
        points={[
          arrow.x + 65,
          arrow.parentY + 65 + (arrow.y - (arrow.parentY + 65)) / 2,
          arrow.x + 65,
          arrow.y - 2,
        ]}
        pointerLength={5}
        pointerWidth={5}
        fill="#a39191"
        stroke="#a39191"
        strokeWidth={1}
        dash={[4, 2]}
      />
      <Line
        points={[
          arrow.parentX + 65,
          arrow.parentY + arrow.parentHeight + 2,
          arrow.parentX + 65,
          arrow.parentY + 65 + (arrow.y - (arrow.parentY + 65)) / 2,
          arrow.x + 65,
          arrow.parentY + 65 + (arrow.y - (arrow.parentY + 65)) / 2,
        ]}
        fill="#a39191"
        stroke="#a39191"
        strokeWidth={1}
        dash={[4, 2]}
      />
    </>
  );

  const LeftDownArrow: React.FC<ArrowProps> = ({ arrow }) => (
    <>
      <Arrow
        points={[
          arrow.x + 65,
          arrow.parentY + 65 + (arrow.y - (arrow.parentY + 65)) / 2,
          arrow.x + 65,
          arrow.y - 2,
        ]}
        pointerLength={5}
        pointerWidth={5}
        fill="#a39191"
        stroke="#a39191"
        strokeWidth={1}
        dash={[4, 2]}
      />
      <Line
        points={[
          arrow.parentX + 65,
          arrow.parentY + arrow.parentHeight + 2,
          arrow.parentX + 65,
          arrow.parentY + 65 + (arrow.y - (arrow.parentY + 65)) / 2,
          arrow.x + 65,
          arrow.parentY + 65 + (arrow.y - (arrow.parentY + 65)) / 2,
        ]}
        fill="#a39191"
        stroke="#a39191"
        strokeWidth={1}
        dash={[4, 2]}
      />
    </>
  );

  const RightUpArrow: React.FC<ArrowProps> = ({ arrow }) => (
    <>
      <Arrow
        points={[
          arrow.x + 65,
          arrow.parentY - arrow.parentHeight / 2 - (arrow.parentY - arrow.y),
          arrow.x + 65,
          arrow.y,
        ]}
        pointerLength={5}
        pointerWidth={5}
        fill="#a39191"
        stroke="#a39191"
        strokeWidth={1}
        dash={[4, 2]}
      />
      <Line
        points={[
          arrow.parentX + 130,
          arrow.parentY + arrow.parentHeight / 2,
          arrow.parentX + 160,
          arrow.parentY + arrow.parentHeight / 2,
          arrow.parentX + 160,
          arrow.parentY - arrow.parentHeight / 2 - (arrow.parentY - arrow.y),
          arrow.x + 65,
          arrow.parentY - arrow.parentHeight / 2 - (arrow.parentY - arrow.y),
        ]}
        fill="#a39191"
        stroke="#a39191"
        strokeWidth={1}
        dash={[4, 2]}
      />
    </>
  );
  const LeftUpArrow: React.FC<ArrowProps> = ({ arrow }) => (
    <>
      <Arrow
        points={[
          arrow.x + 65,
          arrow.parentY - arrow.parentHeight / 2 - (arrow.parentY - arrow.y),
          arrow.x + 65,
          arrow.y,
        ]}
        pointerLength={5}
        pointerWidth={5}
        fill="#a39191"
        stroke="#a39191"
        strokeWidth={1}
        dash={[4, 2]}
      />
      <Line
        points={[
          arrow.parentX + 130,
          arrow.parentY + arrow.parentHeight / 2,
          arrow.parentX + 160,
          arrow.parentY + arrow.parentHeight / 2,
          arrow.parentX + 160,
          arrow.parentY - arrow.parentHeight / 2 - (arrow.parentY - arrow.y),
          arrow.x + 65,
          arrow.parentY - arrow.parentHeight / 2 - (arrow.parentY - arrow.y),
        ]}
        fill="#a39191"
        stroke="#a39191"
        strokeWidth={1}
        dash={[4, 2]}
      />
    </>
  );

  const SLeftUpArrow: React.FC<ArrowProps> = ({ arrow }) => (
    <>
      <Arrow
        points={[
          arrow.parentX - 65,
          arrow.y + arrow.height / 2,
          arrow.x + 130,
          arrow.y + arrow.height / 2,
        ]}
        pointerLength={5}
        pointerWidth={5}
        fill="#a39191"
        stroke="#a39191"
        strokeWidth={1}
        dash={[4, 2]}
      />
      <Line
        points={[
          arrow.parentX,
          arrow.parentY + arrow.parentHeight / 2,
          arrow.parentX - 65,
          arrow.parentY + arrow.parentHeight / 2,
          arrow.parentX - 65,
          arrow.y + arrow.height / 2,
        ]}
        fill="#a39191"
        stroke="#a39191"
        strokeWidth={1}
        dash={[4, 2]}
      />
    </>
  );

  const SUpArrow: React.FC<ArrowProps> = ({ arrow }) => (
    <>
      <Arrow
        points={[
          arrow.parentX - 10,
          arrow.y + arrow.height / 2,
          arrow.x,
          arrow.y + arrow.height / 2,
        ]}
        pointerLength={5}
        pointerWidth={5}
        fill="#a39191"
        stroke="#a39191"
        strokeWidth={1}
        dash={[4, 2]}
      />
      <Line
        points={[
          arrow.parentX,
          arrow.parentY + arrow.parentHeight / 2,
          arrow.parentX - 10,
          arrow.parentY + arrow.parentHeight / 2,
          arrow.parentX - 10,
          arrow.y + arrow.height / 2,
        ]}
        fill="#a39191"
        stroke="#a39191"
        strokeWidth={1}
        dash={[4, 2]}
      />
    </>
  );

  const CustomArrow: React.FC<ArrowProps> = ({ arrow }) =>
    arrow.customLine ? (
      <>
        <Arrow
          points={arrow.customLine}
          pointerLength={5}
          pointerWidth={5}
          fill="#a39191"
          stroke="#a39191"
          strokeWidth={1}
          dash={[4, 2]}
        />
      </>
    ) : null;

  const displayArrows = arrowsState
    ? arrowsState.map((arrow, index) => {
        if (arrow.straightLine === 'custom' && arrow.customLine) {
          // Custom arrow
          return <CustomArrow key={`arrow-line${index}`} arrow={arrow} />;
        } else if (
          (arrow.parentX === arrow.x || arrow.straightLine === true) &&
          arrow.parentY > arrow.y &&
          (arrow.parentY - arrow.y < 200 || arrow.straightLine === true)
        ) {
          // Streight arrow direction to top
          return <UpArrow key={`arrow-line${index}`} arrow={arrow} />;
        } else if (
          arrow.parentX === arrow.x &&
          arrow.parentY > arrow.y &&
          arrow.parentY - arrow.y >= 200
        ) {
          // Left up right arrow
          return <SUpArrow key={`arrow-line${index}`} arrow={arrow} />;
        } else if (
          (arrow.parentX === arrow.x || arrow.straightLine === true) &&
          arrow.parentY < arrow.y
        ) {
          // Streight arrow direction to bottom
          return <DownArrow key={`arrow-line${index}`} arrow={arrow} />;
        } else if (
          (arrow.parentHeight + arrow.parentY - (arrow.height + arrow.y) === 0 ||
            (arrow.parentHeight + arrow.parentY - (arrow.height + arrow.y) <= 28 &&
              arrow.parentHeight + arrow.parentY - (arrow.height + arrow.y) >= -28)) &&
          arrow.parentX < arrow.x
        ) {
          // Streight arrow direction to right
          return <RightArrow key={`arrow-${index}`} arrow={arrow} />;
        } else if (
          (arrow.parentHeight + arrow.parentY - (arrow.height + arrow.y) === 0 ||
            (arrow.parentHeight + arrow.parentY - (arrow.height + arrow.y) <= 28 &&
              arrow.parentHeight + arrow.parentY - (arrow.height + arrow.y) >= -28)) &&
          arrow.parentX > arrow.x
        ) {
          // Streight arrow direction to left
          return <LeftArrow key={`arrow-${index}`} arrow={arrow} />;
        } else if (arrow.parentX < arrow.x && arrow.parentY < arrow.y) {
          // Right down arrow
          return <RightDownArrow key={`arrow-line${index}`} arrow={arrow} />;
        } else if (arrow.parentX > arrow.x && arrow.parentY < arrow.y) {
          // Left down arrow
          return <LeftDownArrow key={`arrow-line${index}`} arrow={arrow} />;
        } else if (arrow.parentX < arrow.x && arrow.parentY > arrow.y) {
          // Right up arrow
          return <RightUpArrow key={`arrow-line${index}`} arrow={arrow} />;
        } else if (
          arrow.parentX > arrow.x &&
          arrow.parentY > arrow.y &&
          arrow.parentX - arrow.x <= 100
        ) {
          // Left up arrow
          return <LeftUpArrow key={`arrow-line${index}`} arrow={arrow} />;
        } else if (
          arrow.parentX > arrow.x &&
          arrow.parentY > arrow.y &&
          arrow.parentX - arrow.x > 100
        ) {
          // Left up down arrow
          return <SLeftUpArrow key={`arrow-line${index}`} arrow={arrow} />;
        } else {
          // Default arrow
          return (
            <Arrow
              key={`arrow-${index}`}
              points={[
                arrow.parentX + 130,
                arrow.parentY + arrow.parentHeight / 2,
                arrow.x,
                arrow.y + arrow.height / 2,
              ]}
              pointerLength={5}
              pointerWidth={5}
              fill="#a39191"
              stroke="#a39191"
              strokeWidth={1}
              dash={[4, 2]}
            />
          );
        }
      })
    : null;

  return (
    <div ref={chartRef} className="flow-chart">
      <Stage
        width={diagramSize.width}
        height={diagramSize.height}
        draggable={!isMobile}
        position={{ x: 0, y: 0 }}
        dragBoundFunc={dragHandler}
      >
        <Layer>
          {displayArrows}
          {displaySteps}
        </Layer>
      </Stage>
    </div>
  );
};

export default FlowChartCanvas;
